import BaseChart from '@components/charts/BaseChart';
import BaseCompactChart from '@components/charts/BaseCompactChart';
import config from '@config';
import type { ChartData } from 'types/common';

interface Props {
  colors: readonly string[];
  compact?: boolean;
  data: ChartData[];
  gridHorizontal?: boolean;
  gridVertical?: boolean;
  width?: number;
  height?: number;
  onSelectDataPoint?: (data: ChartData) => void;
}

function Chart({ compact, data, ...restProps }: Props) {
  // Define the number of ticks
  const tickCount = 10;

  // Convert to thousands
  const normalizedData = [
    ...data.map((entry) => ({ ...entry, value: Math.abs(entry.value / 1000) }))
  ];

  // Find the maximum value for setting the ticks
  const maxValue = Math.max(...normalizedData.map(({ value }) => value));

  // Calculate tick step
  let tickStep = Math.ceil(maxValue / tickCount / 1000) * 1000;

  // Generate ticks
  const ticks = Array.from({ length: tickCount + 1 }).map(
    (_, index) => Math.ceil((index * tickStep) / 1000) * 1000
  );

  // Unique ticks for x-axis (remove duplicates)
  const xAxisTicks = Array.from(new Set(ticks));

  if (compact) {
    // Same ticks for the compact chart
    const yAxisTicks = [0, ...xAxisTicks.filter((_, index) => index % 2 === 0)];

    // A hack to get rid of React's 'unique key' warning
    yAxisTicks[1] = 0.000000001;

    // Format the ticks to ensure they are readable
    const formatTick = (tick: number, index: number) =>
      tick > yAxisTicks[0] && (index + 1) % 2 !== 0 ? adjustUnit(tick) : '';

    return (
      <BaseCompactChart
        {...restProps}
        className="universe-chart"
        data={normalizedData}
        height={config.chart.compact.height}
        width={config.chart.compact.width}
        gridVertical={false}
        domain={[yAxisTicks.at(0), yAxisTicks.at(-1)]}
        yAxisTicks={yAxisTicks}
        overridenSpecs={{
          margin: {
            top: 10,
            right: -14,
            bottom: 1,
            left: 0
          },
          tickSize: 23,
          tickAnchor: 'middle'
        }}
        formatLabel={formatLabel}
        formatTick={formatTick}
      />
    );
  } else {
    // Format the ticks to ensure they are readable
    const formatTick = (tick: number, index: number) => {
      if (xAxisTicks.length > 8 && index % 2 === 0) {
        return '';
      }

      return tick > xAxisTicks.at(0) && tick < xAxisTicks.at(-1)
        ? adjustUnit(tick)
        : '';
    };

    return (
      <BaseChart
        {...restProps}
        data={normalizedData}
        domain={[xAxisTicks.at(0), xAxisTicks.at(-1)]}
        xAxisTicks={xAxisTicks}
        overridenSpecs={{
          margin: {
            top: 0,
            right: 54,
            bottom: -14,
            left: 0
          }
        }}
        formatLabel={formatLabel}
        formatTick={formatTick}
      />
    );
  }
}

function adjustUnit(value: number) {
  if (value / 1000 > 99.9) {
    return `${Number((value / Math.pow(10, 6)).toFixed(2))}B`;
  }

  return `${value / 1000}M`;
}

function formatLabel(value: string) {
  // Convert to millions
  let numericValue = Number(value) / 1000;
  let unitSuffix = 'M';

  if (numericValue > 999.9) {
    // Convert to billions
    numericValue = numericValue / 1000;
    unitSuffix = 'B';
  }

  return `${Math.floor(numericValue * 10) / 10}${unitSuffix}`;
}

export default Chart;
